import { Fragment } from "react";
import {
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import CustomFieldCreator from "../../../components/CustomFieldCreator";
import {
  HIDDEN_FIELD_TYPE,
  isHiddenFieldType,
} from "../../../constants/fieldTypes";
import { useState } from "react";
import Tooltip from "../../../components/Tooltip";
import DeleteAlert from "../../../components/DeleteAlert";
import RichTextEditor from "../../../components/RichTextEditor";
import { useLanguage } from "../../../contexts/LanguageContext";
import Button from "../../../components/Button";

export default function FormBuilder({
  formType,
  description,
  submitText,
  fields,
  formData,
  selectedPass,
  isSaving,
  error,
  selectedStore,
  passes,
  editingFieldId,
  originalFieldState,
  showOptionsMap,
  optionsState,
  isMarketingFieldEditing,
  PREDEFINED_FIELDS,
  onDescriptionChange,
  onSubmitTextChange,
  onFormDataChange,
  onSelectedPassChange,
  onFieldsChange,
  handleFieldRequirementChange,
  handleAddField,
  handleFieldChange,
  handleSubmit,
  setEditingFieldId,
  setOriginalFieldState,
  setShowOptionsMap,
  setOptionsState,
  setIsMarketingFieldEditing,
  updateCustomFieldMutation,
  deleteCustomFieldMutation,
  isFieldUsedInPassLayout,
  isFieldUsedInAnyPassLayout,
  addFieldIfNotExists,
  passesLoading,
  editMode,
  formT, // the form's own language; only the edit page has one
}) {
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [deleteCustomFieldId, setDeleteCustomFieldId] = useState(null);

  // The form's own language when the caller supplies it (edit page), else
  // the merchant's dashboard language (create page, which has no form language
  // to render in yet).
  const { t } = useLanguage();
  const translationFunction = formT || t;

  return (
    <div className="bg-white w-full overflow-hidden rounded-2xl shadow-xs p-4 md:p-6 lg:p-8">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Pass Selector */}
        {formType === 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translationFunction("forms.edit.selectPass.label")}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <select
              value={selectedPass?.id}
              onChange={(e) => {
                const passId = parseInt(e.target.value, 10);
                const pass = passes?.find((p) => p.id === passId);
                onSelectedPassChange(pass);
              }}
              className="w-full rounded-lg border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
              required
            >
              <option key="selectPass" value="">
                {translationFunction("forms.edit.selectPass.label")}
              </option>
              {passes?.map((pass) => (
                <option key={pass.id} value={pass.id}>
                  {pass.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {translationFunction("forms.edit.selectPass.description")}
            </p>
          </div>
        )}

        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translationFunction("forms.edit.formDescription.label")}
          </label>
          <RichTextEditor
            content={description}
            onChange={(html) => onDescriptionChange(html)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translationFunction("forms.edit.submitButton.label")}
          </label>
          <input
            type="text"
            value={submitText}
            onChange={(e) => onSubmitTextChange(e.target.value)}
            className="w-full rounded-lg border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
            placeholder={translationFunction(
              "forms.edit.submitButton.placeholder",
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {translationFunction("forms.new.initial.colors.background")}
            </label>
            <input
              type="color"
              value={formData.backgroundColor}
              onChange={(e) =>
                onFormDataChange((prev) => ({
                  ...prev,
                  backgroundColor: e.target.value,
                }))
              }
              className="mt-1 block w-full h-9 rounded-md border-gray-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {translationFunction("forms.new.initial.colors.text")}
            </label>
            <input
              type="color"
              value={formData.textColor}
              onChange={(e) =>
                onFormDataChange((prev) => ({
                  ...prev,
                  textColor: e.target.value,
                }))
              }
              className="mt-1 block w-full h-9 rounded-md border-gray-300"
            />
          </div>
        </div>

        <div className="space-y-6">
          {/* Predefined Fields Table */}
          <div className="overflow-hidden border border-gray-200 rounded-xl shadow-xs">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  {translationFunction("forms.new.fields.selectUnique")}
                </span>
                <div className="flex items-center space-x-6">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="uniqueField"
                      checked={fields.some(
                        (f) => f.type === "email" && f.unique,
                      )}
                      onChange={() => {
                        const newFields = fields.map((field) => {
                          if (field.type === "email") {
                            // Ensure email field is enabled and unique
                            return {
                              ...field,
                              unique: true,
                              required: true,
                            };
                          } else if (field.type === "tel") {
                            // Remove unique from phone
                            return {
                              ...field,
                              unique: false,
                            };
                          }
                          return field;
                        });

                        // If email field doesn't exist, add it
                        if (!newFields.some((f) => f.type === "email")) {
                          newFields.push({
                            ...PREDEFINED_FIELDS.email,
                            unique: true,
                            required: true,
                          });
                        }

                        onFieldsChange(newFields);
                      }}
                      className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {translationFunction("forms.new.fields.types.email")}
                    </span>
                  </label>
                  <label
                    className={`inline-flex items-center ${!fields.some((f) => f.type === "tel") ? "opacity-50" : ""}`}
                  >
                    <input
                      type="radio"
                      name="uniqueField"
                      checked={fields.some((f) => f.type === "tel" && f.unique)}
                      disabled={!fields.some((f) => f.type === "tel")}
                      onChange={() => {
                        const newFields = fields.map((field) => {
                          if (field.type === "tel") {
                            // Ensure phone field is enabled and unique
                            return {
                              ...field,
                              unique: true,
                              required: true,
                            };
                          } else if (field.type === "email") {
                            // Remove unique from email
                            return {
                              ...field,
                              unique: false,
                            };
                          }
                          return field;
                        });

                        // If phone field doesn't exist, add it
                        if (!newFields.some((f) => f.type === "tel")) {
                          newFields.push({
                            ...PREDEFINED_FIELDS.phone,
                            unique: true,
                            required: true,
                          });
                        }

                        onFieldsChange(newFields);
                      }}
                      className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {translationFunction("forms.new.fields.types.phone")}
                    </span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {translationFunction(
                    "forms.new.fields.uniqueFieldDescription",
                  )}
                </p>
              </div>
            </div>
            <div className="max-w-8xl mx-auto overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300 text-sm text-left">
                <thead className="bg-gray-50">
                  <tr className="text-gray-500 text-xs uppercase tracking-wider">
                    <th scope="col" className="p-3 pl-4 font-medium w-1/2">
                      {translationFunction("forms.new.fields.fieldType")}
                    </th>
                    <th
                      scope="col"
                      className="p-3 font-medium text-center w-1/6"
                    >
                      {translationFunction("forms.new.fields.enabled")}
                    </th>
                    <th
                      scope="col"
                      className="p-3 font-medium text-center w-1/6"
                    >
                      {translationFunction("forms.new.fields.required")}
                    </th>
                    <th scope="col" className="p-3 pr-4 font-medium w-1/6"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 text-gray-900 font-medium">
                  {Object.entries(PREDEFINED_FIELDS).map(([key, field]) => {
                    const existingField = fields.find(
                      (f) => f.type === field.type && f.label === field.label,
                    );
                    const isEnabled = !!existingField;
                    const isEmailField = field.type === "email";
                    const isPhoneField = field.type === "tel";
                    const isEmailUnique = fields.some(
                      (f) => f.type === "email" && f.unique,
                    );
                    const isPhoneUnique = fields.some(
                      (f) => f.type === "tel" && f.unique,
                    );
                    // Field should be disabled when it's unique
                    const isDisabled =
                      (isEmailField && isEmailUnique) ||
                      (isPhoneField && isPhoneUnique);

                    return (
                      <Fragment key={key}>
                        <tr>
                          <td className="p-3 pl-4">
                            <div className="flex items-center">
                              {translationFunction(
                                `forms.new.fields.types.${key}`,
                              )}
                            </div>
                          </td>
                          <td
                            className={`p-3 text-center ${isDisabled ? "opacity-50" : ""}`}
                          >
                            <button
                              type="button"
                              disabled={
                                (field.type === "email" &&
                                  fields.some(
                                    (f) =>
                                      f.type === "email" &&
                                      f.unique &&
                                      field.type === "email",
                                  )) ||
                                (field.type === "tel" &&
                                  fields.some(
                                    (f) =>
                                      f.type === "tel" &&
                                      f.unique &&
                                      field.type === "tel",
                                  ))
                              }
                              onClick={() => {
                                if (isEnabled) {
                                  // Check if this field is currently unique
                                  const isCurrentlyUnique = fields.find(
                                    (f) =>
                                      f.type === field.type &&
                                      f.label === field.label,
                                  )?.unique;

                                  // Don't allow disabling if this field is unique
                                  if (isCurrentlyUnique) {
                                    return;
                                  }

                                  // Store the current state and remove the field
                                  const currentField = fields.find(
                                    (f) =>
                                      f.type === field.type &&
                                      f.label === field.label,
                                  );
                                  setOriginalFieldState({
                                    ...currentField,
                                    wasEnabled: true,
                                    wasRequired: currentField.required,
                                  });
                                  onFieldsChange(
                                    fields.filter(
                                      (f) =>
                                        !(
                                          f.type === field.type &&
                                          f.label === field.label
                                        ),
                                    ),
                                  );

                                  if (field.field === "marketing") {
                                    setIsMarketingFieldEditing(false);
                                  }
                                } else {
                                  // Enable the field
                                  const newField = {
                                    ...field,
                                    unique: false,
                                    required: originalFieldState?.wasEnabled
                                      ? originalFieldState.wasRequired
                                      : false,
                                    options: field.options || [],
                                  };
                                  onFieldsChange([...fields, newField]);
                                }
                              }}
                              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${isEnabled ? "bg-indigo-600" : "bg-gray-200"}`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${isEnabled ? "translate-x-5" : "translate-x-0"}`}
                              />
                            </button>
                          </td>
                          <td
                            className={`p-3 text-center ${isDisabled ? "opacity-50" : ""}`}
                          >
                            {field.field != "marketing" && (
                              <input
                                type="checkbox"
                                checked={existingField?.required || false}
                                disabled={
                                  !isEnabled ||
                                  (isEmailField && isEmailUnique) ||
                                  (isPhoneField && isPhoneUnique)
                                }
                                onChange={(e) => {
                                  handleFieldRequirementChange(
                                    isEnabled,
                                    field,
                                    e.target.checked,
                                  );
                                }}
                                className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                              />
                            )}
                          </td>
                          <td className="p-3 pr-4 text-right">
                            {field.field === "marketing" && isEnabled && (
                              <button
                                type="button"
                                onClick={() =>
                                  setIsMarketingFieldEditing(
                                    !isMarketingFieldEditing,
                                  )
                                }
                                className="ml-2 text-indigo-600 hover:text-indigo-900"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                            )}
                          </td>
                        </tr>
                        {field.field === "marketing" &&
                          isEnabled &&
                          isMarketingFieldEditing && (
                            <tr className="bg-gray-50">
                              <td colSpan="4" className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <input
                                    type="text"
                                    value={
                                      existingField.options?.[0] ||
                                      translationFunction(
                                        "forms.new.initial.marketing",
                                      )
                                    }
                                    onChange={(e) =>
                                      handleFieldChange(
                                        fields.indexOf(existingField),
                                        {
                                          ...existingField,
                                          options: [e.target.value],
                                        },
                                      )
                                    }
                                    className="flex-1 rounded-lg border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                                    placeholder={translationFunction(
                                      "forms.new.initial.marketing",
                                    )}
                                  />

                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleFieldChange(
                                        fields.indexOf(existingField),
                                        {
                                          ...existingField,
                                          options: [
                                            translationFunction(
                                              "forms.new.initial.marketing",
                                            ),
                                          ],
                                        },
                                      );
                                    }}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                                  >
                                    <ArrowPathIcon className="h-5 w-5 mr-2" />
                                    {translationFunction("common.reset")}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Store Custom Fields Section */}
          {selectedStore?.custom_fields &&
            selectedStore.custom_fields.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium text-gray-900">
                    {translationFunction("forms.new.fields.storeCustomFields")}
                  </h3>
                  <span className="text-sm text-gray-500">
                    {translationFunction(
                      "forms.new.fields.storeCustomFieldsDescription",
                    )}
                  </span>
                </div>
                <div className="overflow-hidden border border-gray-200 rounded-xl shadow-xs">
                  <div className="max-w-8xl mx-auto overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-300 text-sm text-left">
                      <thead className="bg-gray-50">
                        <tr className="text-gray-500 text-xs uppercase tracking-wider">
                          <th
                            scope="col"
                            className="p-3 pl-4 font-medium w-1/2"
                          >
                            {translationFunction("forms.new.fields.fieldType")}
                          </th>
                          <th
                            scope="col"
                            className="p-3 font-medium text-center w-1/6"
                          >
                            {translationFunction("forms.new.fields.enabled")}
                          </th>
                          <th
                            scope="col"
                            className="p-3 font-medium text-center w-1/6"
                          >
                            {translationFunction("forms.new.fields.required")}
                          </th>
                          <th
                            scope="col"
                            className="p-3 pr-4 font-medium w-1/6"
                          ></th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200 text-gray-900 font-medium">
                        {selectedStore.custom_fields.map((storeField) => {
                          const existingField = fields.find(
                            (f) =>
                              f.type === storeField.field_type &&
                              f.label === storeField.label,
                          );
                          let isEnabled = !!existingField;
                          const isEditing = editingFieldId === storeField.id;

                          return (
                            <Fragment key={storeField.id}>
                              {isEditing ? (
                                <tr className="bg-gray-50">
                                  <td colSpan="4" className="px-4 py-3">
                                    <div className="space-y-4">
                                      <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-medium text-gray-900">
                                          Edit Field
                                        </h3>
                                        <div className="flex items-center space-x-2">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const dataToSend = {
                                                label:
                                                  originalFieldState?.label ||
                                                  storeField.label,
                                                field_type:
                                                  storeField.field_type,
                                                // A hidden field's key is the
                                                // query parameter it reads, so
                                                // it travels with the save
                                                // instead of being re-derived
                                                // from the label.
                                                field: storeField.field,
                                                options:
                                                  optionsState[storeField.id] ||
                                                  storeField.options,
                                              };
                                              updateCustomFieldMutation.mutate({
                                                id: storeField.id,
                                                data: dataToSend,
                                              });
                                              setEditingFieldId(null);
                                              setOriginalFieldState(null);
                                            }}
                                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                                          >
                                            {translationFunction("common.save")}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              if (originalFieldState) {
                                                Object.assign(
                                                  storeField,
                                                  originalFieldState,
                                                );
                                                setOptionsState((prev) => ({
                                                  ...prev,
                                                  [storeField.id]:
                                                    originalFieldState.options ||
                                                    [],
                                                }));
                                              }
                                              setEditingFieldId(null);
                                              setOriginalFieldState(null);
                                            }}
                                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm text-gray-700 bg-white hover:bg-gray-50 rounded-md transition-colors"
                                          >
                                            {translationFunction(
                                              "common.cancel",
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-2 gap-3">
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Label
                                          </label>
                                          <input
                                            type="text"
                                            value={
                                              originalFieldState?.label ||
                                              storeField.label
                                            }
                                            className="w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                            onChange={(e) => {
                                              setOriginalFieldState({
                                                ...originalFieldState,
                                                label: e.target.value,
                                              });
                                            }}
                                            onKeyDown={(e) => {
                                              if (e.key === "Escape") {
                                                setEditingFieldId(null);
                                                setOriginalFieldState(null);
                                              }
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Field Type
                                          </label>
                                          <select
                                            defaultValue={storeField.field_type}
                                            onChange={(e) => {
                                              const newType = e.target.value;
                                              const newStoreField = {
                                                ...storeField,
                                                field_type: newType,
                                                options:
                                                  storeField.options || [],
                                              };
                                              Object.assign(
                                                storeField,
                                                newStoreField,
                                              );

                                              // Update visibility state for the new field type
                                              setShowOptionsMap((prev) => ({
                                                ...prev,
                                                [storeField.id]: [
                                                  "select",
                                                  "radio",
                                                  "checkbox",
                                                ].includes(newType),
                                              }));

                                              setEditingFieldId(storeField.id);
                                            }}
                                            className="w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                          >
                                            <option key="text" value="text">
                                              {translationFunction(
                                                "forms.new.fields.types.text",
                                              )}
                                            </option>
                                            <option key="date" value="date">
                                              {translationFunction(
                                                "forms.new.fields.types.date",
                                              )}
                                            </option>
                                            <option key="select" value="select">
                                              {translationFunction(
                                                "forms.new.fields.types.select",
                                              )}
                                            </option>
                                            <option key="radio" value="radio">
                                              {translationFunction(
                                                "forms.new.fields.types.radio",
                                              )}
                                            </option>
                                            <option
                                              key="checkbox"
                                              value="checkbox"
                                            >
                                              {translationFunction(
                                                "forms.new.fields.types.checkbox",
                                              )}
                                            </option>
                                            <option
                                              key={HIDDEN_FIELD_TYPE}
                                              value={HIDDEN_FIELD_TYPE}
                                            >
                                              {translationFunction(
                                                "forms.new.fields.types.hidden",
                                              )}
                                            </option>
                                          </select>
                                        </div>
                                      </div>
                                      {isHiddenFieldType(
                                        storeField.field_type,
                                      ) && (
                                        <div className="mt-4">
                                          <label className="block text-sm font-medium text-gray-700 mb-1">
                                            {translationFunction(
                                              "forms.new.fields.urlParameter.label",
                                            )}
                                          </label>
                                          <input
                                            type="text"
                                            defaultValue={storeField.field}
                                            onChange={(e) => {
                                              Object.assign(storeField, {
                                                field: e.target.value.trim(),
                                              });
                                              setEditingFieldId(storeField.id);
                                            }}
                                            className="w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                          />
                                          <p className="text-xs text-gray-500 mt-1">
                                            {translationFunction(
                                              "forms.new.fields.urlParameter.description",
                                            )}
                                          </p>
                                        </div>
                                      )}
                                      {["select", "radio", "checkbox"].includes(
                                        storeField.field_type,
                                      ) &&
                                        showOptionsMap[storeField.id] && (
                                          <div className="mt-4">
                                            <div className="text-sm font-medium text-gray-700 mb-2">
                                              {translationFunction(
                                                "forms.new.fields.options.label",
                                              )}
                                            </div>
                                            <div className="space-y-2">
                                              {(
                                                optionsState[storeField.id] ||
                                                storeField.options ||
                                                []
                                              ).map((option, optionIndex) => (
                                                <div
                                                  key={`field-${storeField.id}-option-${optionIndex}`}
                                                  className="flex items-center space-x-2"
                                                >
                                                  <input
                                                    type="text"
                                                    value={
                                                      optionsState[
                                                        storeField.id
                                                      ]?.[optionIndex] || option
                                                    }
                                                    onChange={(e) => {
                                                      const newOptions = [
                                                        ...(optionsState[
                                                          storeField.id
                                                        ] ||
                                                          storeField.options ||
                                                          []),
                                                      ];
                                                      newOptions[optionIndex] =
                                                        e.target.value;
                                                      setOptionsState(
                                                        (prev) => ({
                                                          ...prev,
                                                          [storeField.id]:
                                                            newOptions,
                                                        }),
                                                      );
                                                    }}
                                                    className="block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                                    placeholder={translationFunction(
                                                      "forms.new.fields.options.optionPlaceholder",
                                                    )}
                                                  />
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      setOptionsState(
                                                        (prev) => ({
                                                          ...prev,
                                                          [storeField.id]: (
                                                            prev[
                                                              storeField.id
                                                            ] ||
                                                            storeField.options ||
                                                            []
                                                          ).filter(
                                                            (_, i) =>
                                                              i !== optionIndex,
                                                          ),
                                                        }),
                                                      );
                                                    }}
                                                    className="text-red-600 hover:text-red-800"
                                                  >
                                                    <svg
                                                      className="h-5 w-5"
                                                      fill="none"
                                                      viewBox="0 0 24 24"
                                                      stroke="currentColor"
                                                    >
                                                      <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M6 18L18 6M6 6l12 12"
                                                      />
                                                    </svg>
                                                  </button>
                                                </div>
                                              ))}
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setOptionsState((prev) => ({
                                                    ...prev,
                                                    [storeField.id]: [
                                                      ...(prev[storeField.id] ||
                                                        storeField.options ||
                                                        []),
                                                      "",
                                                    ],
                                                  }));
                                                }}
                                                className="inline-flex items-center px-3 py-1 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors"
                                              >
                                                <svg
                                                  className="h-4 w-4 mr-1"
                                                  fill="none"
                                                  viewBox="0 0 24 24"
                                                  stroke="currentColor"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 4v16m8-8H4"
                                                  />
                                                </svg>
                                                {translationFunction(
                                                  "forms.new.fields.options.addOption",
                                                )}
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                <tr>
                                  <td className="p-3 pl-4">
                                    <p className="w-full overflow-hidden text-ellipsis">
                                      {storeField.label}
                                      {isFieldUsedInPassLayout(storeField) && (
                                        <span className="ml-2 inline-flex items-center w-1/3 px-2 py-0.5 rounded-sm text-xs font-medium bg-indigo-100 text-indigo-800">
                                          {selectedPass.pass.header_field ===
                                          storeField.label
                                            ? translationFunction(
                                                "forms.fields.cardPosition.header",
                                              )
                                            : selectedPass.pass.left_field ===
                                                storeField.label
                                              ? translationFunction(
                                                  "forms.fields.cardPosition.left",
                                                )
                                              : selectedPass.pass
                                                    .right_field ===
                                                  storeField.label
                                                ? translationFunction(
                                                    "forms.fields.cardPosition.right",
                                                  )
                                                : ""}
                                        </span>
                                      )}
                                    </p>
                                  </td>
                                  {!passesLoading && (
                                    <>
                                      <td className="p-3 text-center">
                                        <button
                                          type="button"
                                          disabled={isFieldUsedInPassLayout(
                                            storeField,
                                          )}
                                          onClick={() => {
                                            if (isEnabled) {
                                              // Store the current field state before disabling
                                              const currentField = fields.find(
                                                (f) =>
                                                  f.type ===
                                                    storeField.field_type &&
                                                  f.label === storeField.label,
                                              );
                                              // Store in originalFieldState to preserve the state
                                              setOriginalFieldState({
                                                ...currentField,
                                                wasEnabled: true,
                                                wasRequired:
                                                  currentField.required,
                                              });
                                              onFieldsChange(
                                                fields.filter(
                                                  (f) =>
                                                    !(
                                                      f.type ===
                                                        storeField.field_type &&
                                                      f.label ===
                                                        storeField.label
                                                    ),
                                                ),
                                              );
                                            } else {
                                              // Check if a field with the same type and label already exists
                                              const fieldExists = fields.some(
                                                (f) =>
                                                  f.type ===
                                                    storeField.field_type &&
                                                  f.label === storeField.label,
                                              );

                                              if (!fieldExists) {
                                                addFieldIfNotExists(
                                                  storeField,
                                                  originalFieldState,
                                                );
                                              }
                                            }
                                          }}
                                          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${isEnabled ? "bg-indigo-600" : "bg-gray-200"} ${isFieldUsedInPassLayout(storeField) ? "opacity-50 cursor-not-allowed" : ""}`}
                                        >
                                          <span
                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${isEnabled ? "translate-x-5" : "translate-x-0"}`}
                                          />
                                        </button>
                                      </td>
                                      <td className="p-3 text-center">
                                        <input
                                          type="checkbox"
                                          checked={
                                            existingField?.required || false
                                          }
                                          disabled={
                                            !isEnabled ||
                                            isFieldUsedInPassLayout(storeField)
                                          }
                                          onChange={(e) => {
                                            handleFieldRequirementChange(
                                              isEnabled,
                                              storeField,
                                              e.target.checked,
                                            );
                                          }}
                                          className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                                        />
                                      </td>
                                      <td
                                        className={`p-3 pr-4 text-right ${isFieldUsedInAnyPassLayout(storeField) ? "opacity-50 cursor-not-allowed" : ""}`}
                                      >
                                        <Tooltip
                                          text={
                                            isFieldUsedInAnyPassLayout(
                                              storeField,
                                            )
                                              ? translationFunction(
                                                  "forms.fields.disabled.usedInPass",
                                                )
                                              : null
                                          }
                                          className="ml-1"
                                        >
                                          <div className="flex items-center space-x-3 justify-end">
                                            <button
                                              disabled={isFieldUsedInAnyPassLayout(
                                                storeField,
                                              )}
                                              type="button"
                                              onClick={() => {
                                                // Store original state when starting to edit
                                                setOriginalFieldState({
                                                  ...storeField,
                                                });
                                                // Initialize the visibility state based on the current field type
                                                setShowOptionsMap((prev) => ({
                                                  ...prev,
                                                  [storeField.id]: [
                                                    "select",
                                                    "radio",
                                                    "checkbox",
                                                  ].includes(
                                                    storeField.field_type,
                                                  ),
                                                }));
                                                setEditingFieldId(
                                                  storeField.id,
                                                );
                                              }}
                                              className="text-indigo-600 hover:text-indigo-900"
                                            >
                                              <PencilIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                              type="button"
                                              disabled={isFieldUsedInAnyPassLayout(
                                                storeField,
                                              )}
                                              onClick={() => {
                                                setIsDeleteAlertOpen(true);
                                                setDeleteCustomFieldId(
                                                  storeField.id,
                                                );
                                              }}
                                              className="text-red-600 hover:text-red-900"
                                            >
                                              <TrashIcon className="h-5 w-5" />
                                            </button>
                                          </div>
                                        </Tooltip>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

          {/* Custom Fields Section */}
          {fields.length > 0 && (
            <div className="mt-8">
              {fields.filter(
                (field) =>
                  !Object.values(PREDEFINED_FIELDS).some(
                    (f) => f.type === field.type && f.label === field.label,
                  ) &&
                  !selectedStore?.custom_fields?.some(
                    (f) =>
                      f.field_type === field.type && f.label === field.label,
                  ),
              ).length > 0 && (
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {translationFunction("forms.new.fields.customFields")}
                </h3>
              )}
              {fields
                .filter(
                  (field) =>
                    !Object.values(PREDEFINED_FIELDS).some(
                      (f) => f.type === field.type && f.label === field.label,
                    ) &&
                    !selectedStore?.custom_fields?.some(
                      (f) =>
                        f.field_type === field.type && f.label === field.label,
                    ),
                )
                .map((field, index) => (
                  <CustomFieldCreator
                    key={index}
                    field={field}
                    onFieldChange={(updatedField) => {
                      const newFields = [...fields];
                      const actualIndex = fields.findIndex((f) => f === field);
                      newFields[actualIndex] = updatedField;
                      onFieldsChange(newFields);
                    }}
                    onRemoveField={() => {
                      const actualIndex = fields.findIndex((f) => f === field);
                      const newFields = [...fields];
                      newFields.splice(actualIndex, 1);
                      onFieldsChange(newFields);
                    }}
                  />
                ))}
            </div>
          )}
        </div>

        <div className="flex gap-4 justify-between pt-4">
          <Button
            type="button"
            variant="secondary"
            icon={PlusIcon}
            onClick={handleAddField}
          >
            {translationFunction("forms.edit.fields.addButton")}
          </Button>
          <Button type="submit" isSaving={isSaving}>
            {editMode
              ? translationFunction("forms.edit.button")
              : translationFunction("forms.new.button")}
          </Button>
        </div>
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </form>
      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onConfirm={() => {
          deleteCustomFieldMutation.mutate(deleteCustomFieldId);
          setIsDeleteAlertOpen(false);
        }}
      />
    </div>
  );
}
